// Local AI adapter — img2img against a generator running on the USER'S machine
// (AUTOMATIC1111 stable-diffusion-webui or a compatible /sdapi/v1 endpoint).
//
// This is the FREE 'local' provider tier: it never touches the cloud Edge
// Function, costs no credits and needs no login. The cloud Supabase function
// cannot reach `localhost`, so this path runs entirely client-side. When the
// local server is down/unreachable the adapter reports unavailable and the
// CommerceController falls back to the cloud provider.
//
// Depends only on fetch (injectable for tests) + a base URL, so it is
// unit-testable without a real server.

import type { LocalAiPort } from '../bridge/ports';

export interface LocalAiOptions {
  /** Base URL of the local generator, e.g. http://localhost:7860 (no trailing slash needed). */
  baseUrl: string;
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** Probe/generate timeout in ms. Health check uses a short slice of it. */
  timeoutMs?: number;
  /** img2img denoising strength (how much the AI reworks the sketch). Default 0.65. */
  denoisingStrength?: number;
}

/** Strip a data: URL prefix → raw base64 (SD-WebUI expects bare base64 in init_images). */
function toBareBase64(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 && dataUrl.startsWith('data:') ? dataUrl.slice(comma + 1) : dataUrl;
}

/** Wrap a (possibly bare) base64 image string back into a PNG data URL. */
function toDataUrl(image: string): string {
  return image.startsWith('data:') ? image : 'data:image/png;base64,' + image;
}

export class LocalAi implements LocalAiPort {
  private readonly base: string;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;
  private readonly denoising: number;

  constructor(opts: LocalAiOptions) {
    this.base = opts.baseUrl.replace(/\/+$/, '');
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.timeoutMs = opts.timeoutMs ?? 30_000;
    this.denoising = opts.denoisingStrength ?? 0.65;
  }

  private async fetchWithTimeout(url: string, init: RequestInit, ms: number): Promise<Response> {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    try {
      return await this.fetchImpl(url, { ...init, signal: ctrl.signal });
    } finally {
      clearTimeout(timer);
    }
  }

  async isAvailable(): Promise<boolean> {
    try {
      // Cheap reachability probe with a short timeout so a missing local server
      // doesn't stall the UI before we fall back to the cloud.
      const res = await this.fetchWithTimeout(
        `${this.base}/sdapi/v1/options`,
        { method: 'GET' },
        Math.min(this.timeoutMs, 2500),
      );
      return res.ok;
    } catch {
      return false;
    }
  }

  async generate(imageDataUrl: string, prompt: string): Promise<{ imageUrl: string | null }> {
    const res = await this.fetchWithTimeout(
      `${this.base}/sdapi/v1/img2img`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          init_images: [toBareBase64(imageDataUrl)],
          prompt: prompt || 'enhance this drawing, clean lines, vivid colors',
          denoising_strength: this.denoising,
          steps: 20,
          cfg_scale: 7,
        }),
      },
      this.timeoutMs,
    );
    if (!res.ok) throw new Error(`local_ai_${res.status}`);
    const body = (await res.json()) as { images?: string[] };
    const first = body.images && body.images.length > 0 ? body.images[0] : undefined;
    return { imageUrl: first ? toDataUrl(first) : null };
  }
}
